/**
 * VideoCallStateView — the non-video state views of the video-call page.
 *
 * Ports the Flutter `VideoCallScreenState` state widgets, kept out of
 * `VideoCallPage.tsx` so the page stays under the size threshold
 * (`presentation.md` — component size). Each variant mirrors one Flutter widget:
 *
 *   - `calling`        → `_callingAudienceUserWidget` (`video_call_screen.dart:1037-1110`)
 *                        — the caller's "Calling {name}" pre-join screen (avatar +
 *                        name + cancel). For the audience the name label is hidden
 *                        (Flutter colours it transparent, `:1061-1063`).
 *   - `ended`          → `_streamingEndedWidget` (`:739-799`) — "Call ended".
 *   - `audienceNoJoin` → `_audienceUserDidNotJoinWidget` (`:801-861`) —
 *                        "{name} could not join the video call".
 *   - `renewingToken`  → `_renewingTokenWidget` (`:720-737`) — the centred
 *                        "regenerating token…" overlay (no cancel button).
 *
 * Each non-overlay variant carries the round close/cancel button the Flutter
 * widgets share (`Material` + `close.png`, `:763-790`/`:825-852`/`:1075-1102`),
 * which calls `onClose` (the page wires it to `Navigator.goBack()` — Flutter
 * `_onBackPress`, `:196-199`). The `_lastGift` overlay (`:551-691`) is a
 * separate sibling component (`VideoCallLastGift`), not rendered here.
 *
 * Theme/responsive/l10n are mandatory (`presentation.md`); the caller passes the
 * already-localized + interpolated copy so this component holds no l10n logic.
 */

import React from 'react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { UserUtils } from '@/domain/utils/userUtils';
import { VideoCallStateVariant } from '@/domain/enums/videoCall/videoCallStateVariant';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface VideoCallStateViewProps {
  variant: VideoCallStateVariant;
  /**
   * The fully-localized + interpolated message line shown in the centre of the
   * view ("Calling {name}" / "Call ended" / "{name} could not join…" /
   * "regenerating token…"). The page owns the l10n + name interpolation.
   */
  message: string;
  /**
   * The audience's firebase id, used to render their avatar on the `calling`
   * view (Flutter `CircleBorderAvatar` with
   * `generateUserProfileImageUrl(audienceUser.firebaseId)`, `:1048-1053`). Only
   * read for the `calling` variant.
   */
  audienceFirebaseId?: string;
  /**
   * On the `calling` variant the audience side hides the "Calling {name}" label
   * (Flutter paints it transparent, `:1061-1063`) — pass `false` to keep the
   * avatar but drop the text. Defaults to `true`.
   */
  showMessage?: boolean;
  /**
   * Cancel/close handler (Flutter `_onBackPress`). Omitted for the
   * `renewingToken` overlay, which has no cancel button (`:720-737`).
   */
  onClose?: () => void;
  /** `data-qa-id` for the cancel button (e.g. `video-call-cancel`). */
  closeQaId?: string;
}

export const VideoCallStateView: React.FC<VideoCallStateViewProps> = ({
  variant,
  message,
  audienceFirebaseId,
  showMessage = true,
  onClose,
  closeQaId,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // The centred "regenerating token…" overlay (`_renewingTokenWidget`,
  // `:720-737`): a single centred text block, no avatar, no cancel button.
  if (variant === VideoCallStateVariant.renewingToken) {
    return (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: responsive.spacing['3xl'],
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.lg,
            textAlign: 'center',
          }}
        >
          {message}
        </span>
      </div>
    );
  }

  // The three full-screen state views (`_callingAudienceUserWidget` /
  // `_streamingEndedWidget` / `_audienceUserDidNotJoinWidget`) share the same
  // skeleton: a centred body (avatar — calling only — + the message line) and a
  // round close button pinned to the bottom (`Expanded` + `bottomCenter` in
  // Flutter, `:1068-1106` / `:756-794` / `:818-856`).
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Centred body. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: responsive.spacing.sm,
        }}
      >
        {variant === VideoCallStateVariant.calling && audienceFirebaseId && (
          <CachedImage
            src={UserUtils.generateUserProfileImageUrl(audienceFirebaseId)}
            alt=""
            style={{
              width: responsive.avatar.xlarge,
              height: responsive.avatar.xlarge,
              borderRadius: responsive.borderRadius.round,
              objectFit: 'cover',
            }}
          />
        )}
        {showMessage && (
          <span
            style={{
              color: theme.text.primary,
              fontSize: responsive.font.lg,
              textAlign: 'center',
            }}
          >
            {message}
          </span>
        )}
      </div>

      {/* Round close/cancel button, pinned to the bottom (Flutter `Expanded` +
          `Alignment.bottomCenter` + `viewPadding.bottom + 32`, `:1068-1106`). */}
      {onClose && (
        <button
          {...qaAttr({ id: closeQaId ?? 'video-call-cancel' })}
          onClick={onClose}
          style={{
            position: 'absolute',
            bottom: `calc(${responsive.safeArea.bottom} + ${responsive.spacing['2xl']}px)`,
            left: '50%',
            transform: 'translateX(-50%)',
            width: responsive.button.size.large,
            height: responsive.button.size.large,
            borderRadius: responsive.borderRadius.round,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.surface,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <AssetIcon
            path="/assets/images/close.png"
            width={responsive.icon.medium}
            height={responsive.icon.medium}
            color={theme.whiteLight}
          />
        </button>
      )}
    </div>
  );
};
